import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, Mail, MessageSquare, ExternalLink, LogOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Portal from '../Portal/Portal';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';

interface SubscriptionBlockModalProps {
    isOpen: boolean;
}

const SubscriptionBlockModal: React.FC<SubscriptionBlockModalProps> = ({ isOpen }) => {
    const { t } = useTranslation();

    const supportEmail = 'solvion.ai@gmail.com';
    const supportWhatsapp = '5564996474358';
    const whatsappLink = `https://wa.me/${supportWhatsapp}`;
    const subscribeLink = 'https://lp.scalizai.com';

    const handleLogout = async () => {
        try {
            await supabase.auth.signOut();
            toast.success(t('notifications.success.login_success'));
        } catch (error) {
            console.error('Error logging out:', error);
        }
    };

    if (!isOpen) return null;

    return (
        <Portal>
            <motion.div
                className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-md"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
            >
                <motion.div
                    className="relative w-full max-w-lg rounded-xl bg-white dark:bg-gray-800 shadow-2xl border border-gray-200 dark:border-gray-700/50 p-8 flex flex-col gap-6"
                    initial={{ y: 50, opacity: 0, scale: 0.9 }}
                    animate={{ y: 0, opacity: 1, scale: 1 }}
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                >
                    {/* Header with Icon */}
                    <div className="flex flex-col gap-3 items-center text-center">
                        <div className="p-4 rounded-full bg-red-100 dark:bg-red-900/30">
                            <AlertTriangle className="size-10 text-red-600 dark:text-red-400" />
                        </div>
                        <h2 className="text-2xl font-bold text-[#181411] dark:text-white">
                            {t('modals.subscription_blocked_title')}
                        </h2>
                        <p className="text-base text-gray-600 dark:text-gray-400 leading-relaxed">
                            {t('modals.subscription_blocked_message')}
                        </p>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col gap-3">
                        {/* Subscribe Button - Primary Action */}
                        <motion.a
                            href={subscribeLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex h-12 w-full cursor-pointer items-center justify-center gap-2 overflow-hidden rounded-lg bg-gradient-to-r from-[#f98006] to-[#ffb800] text-white text-sm font-bold shadow-md hover:opacity-90 transition-opacity"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                        >
                            <ExternalLink className="size-5" />
                            {t('modals.subscription_go_to_subscribe')}
                        </motion.a>

                        {/* Contact Buttons */}
                        <div className="grid grid-cols-2 gap-3">
                            <motion.a
                                href={`mailto:${supportEmail}`}
                                className="flex h-12 cursor-pointer items-center justify-center gap-2 overflow-hidden rounded-lg bg-blue-600 dark:bg-blue-700 text-white text-sm font-semibold hover:bg-blue-700 dark:hover:bg-blue-800 transition-colors"
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                <Mail className="size-5" />
                                {t('modals.subscription_contact_email')}
                            </motion.a>

                            <motion.a
                                href={whatsappLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex h-12 cursor-pointer items-center justify-center gap-2 overflow-hidden rounded-lg bg-green-600 dark:bg-green-700 text-white text-sm font-semibold hover:bg-green-700 dark:hover:bg-green-800 transition-colors"
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                <MessageSquare className="size-5" />
                                {t('modals.subscription_contact_whatsapp')}
                            </motion.a>
                        </div>

                        {/* Logout Option */}
                        <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                            <p className="text-xs text-gray-500 dark:text-gray-400 text-center mb-3">
                                {t('modals.subscription_or_logout')}
                            </p>
                            <motion.button
                                type="button"
                                onClick={handleLogout}
                                className="flex h-10 w-full cursor-pointer items-center justify-center gap-2 overflow-hidden rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-sm font-semibold hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                <LogOut className="size-4" />
                                {t('modals.logout')}
                            </motion.button>
                        </div>
                    </div>
                </motion.div>
            </motion.div>
        </Portal>
    );
};

export default SubscriptionBlockModal;
